import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import MiniWidget from '../components/MiniWidget';
import { fetchWidgetData, loadSettings, formatRelativeTime } from '../services/api';
import { WidgetConfig, WidgetData, defaultWidgets } from '../services/types';
import { useAuth } from '../auth/AuthProvider';

export default function FocusedDashboard() {
  const { widgetId, '*': subPath } = useParams<{ widgetId: string; '*': string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [widgets] = useState<WidgetConfig[]>(defaultWidgets.filter(w => w.enabled));
  const { user } = useAuth();
  const iframeRefs = useRef<Record<string, HTMLIFrameElement | null>>({});
  const [widgetData, setWidgetData] = useState<Record<string, WidgetData>>({});
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(60);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Track visited widgets (iframes stay loaded) and per-widget filters
  const [visitedWidgets, setVisitedWidgets] = useState<Set<string>>(new Set([widgetId || '']));
  const [widgetFilters, setWidgetFilters] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    if (widgetId) initial[widgetId] = searchParams.get('filter') || '';
    return initial;
  });

  // Get theme from localStorage and re-render when it changes
  const [theme, setTheme] = useState(localStorage.getItem('umt-hud-theme') || 'dark');

  useEffect(() => {
    const handleStorageChange = () => {
      setTheme(localStorage.getItem('umt-hud-theme') || 'dark');
    };
    window.addEventListener('storage', handleStorageChange);
    // Also check periodically for same-tab changes
    const interval = setInterval(() => {
      const currentTheme = localStorage.getItem('umt-hud-theme') || 'dark';
      if (currentTheme !== theme) setTheme(currentTheme);
    }, 100);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [theme]);

  // Add current widget to visited set when widgetId changes
  useEffect(() => {
    if (widgetId && !visitedWidgets.has(widgetId)) {
      setVisitedWidgets(prev => new Set([...prev, widgetId]));
    }
  }, [widgetId, visitedWidgets]);

  // Get the active widget
  const activeWidget = widgets.find(w => w.id === widgetId);
  const settings = loadSettings();

  // Build iframe URL for a specific widget
  const buildIframeUrl = (wId: string, includeDeepLink: boolean = false) => {
    const widget = widgets.find(w => w.id === wId);
    const baseUrl = settings.widgetUrls[wId]?.drillDown || widget?.drillDownUrl || '';
    if (!baseUrl) return '';

    // Start with the base URL
    const url = new URL(baseUrl);

    // If this is the active widget and we have a sub-path, append it
    if (includeDeepLink && subPath) {
      // Append sub-path to the URL pathname
      url.pathname = url.pathname.replace(/\/$/, '') + '/' + subPath;
    }

    // Forward all query params from the current URL (for deep linking)
    if (includeDeepLink) {
      searchParams.forEach((value, key) => {
        // Don't override theme if already set
        if (key !== 'theme') {
          url.searchParams.set(key, value);
        }
      });
    }

    // Apply widget-specific filter if set (from stat clicks)
    const filter = widgetFilters[wId] || '';
    if (filter) url.searchParams.set('filter', filter);

    // Always set theme
    url.searchParams.set('theme', theme);

    return url.toString();
  };

  // Fetch all widget data for mini-widgets in PARALLEL
  const fetchAll = useCallback(async () => {
    setIsRefreshing(true);
    const settings = loadSettings();

    // Fetch all widgets in parallel
    const fetchPromises = widgets.map(async (widget) => {
      const configuredUrl = settings.widgetUrls[widget.id]?.api;
      const widgetWithUrl = configuredUrl
        ? { ...widget, apiEndpoint: configuredUrl }
        : widget;
      const data = await fetchWidgetData(widgetWithUrl);
      return { widgetId: widget.id, data };
    });

    const results = await Promise.all(fetchPromises);

    // Build widget data map
    const newWidgetData: Record<string, WidgetData> = {};
    for (const { widgetId, data } of results) {
      newWidgetData[widgetId] = data;
    }

    setWidgetData(newWidgetData);
    setLastRefresh(new Date());
    setIsRefreshing(false);
  }, [widgets]);

  // Initial fetch and auto-refresh
  useEffect(() => {
    fetchAll();

    if (autoRefresh) {
      const interval = setInterval(fetchAll, refreshInterval * 1000);
      return () => clearInterval(interval);
    }
  }, [fetchAll, autoRefresh, refreshInterval]);

  // Scroll to top when entering focused view or changing widgets
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [widgetId]);

  // Handle mini-widget click - navigate to that widget's focused view
  const handleMiniWidgetClick = (widget: WidgetConfig) => {
    // Don't reset filter - preserve each widget's state
    navigate(`/dashboard/${widget.id}`);
  };

  // Handle stat click - set filter and navigate if needed
  const handleStatClickForWidget = (targetWidgetId: string, filterValue: string) => {
    // Set filter for the target widget
    setWidgetFilters(prev => ({ ...prev, [targetWidgetId]: filterValue }));
    // Navigate to that widget if not already active
    if (targetWidgetId !== widgetId) {
      navigate(`/dashboard/${targetWidgetId}`);
    }
  };

  // Build list of trusted widget origins for postMessage validation
  const trustedOrigins = useMemo(() => {
    const origins = new Set<string>();
    widgets.forEach(widget => {
      try {
        const url = settings.widgetUrls[widget.id]?.drillDown || widget.drillDownUrl;
        if (url) origins.add(new URL(url).origin);
      } catch { /* ignore invalid URLs */ }
    });
    return origins;
  }, [widgets, settings]);

  // Listen for REQUEST_AUTH messages from iframes
  // This allows embedded apps to request auth when they're ready (solves timing issues)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verify origin is a trusted widget
      if (!trustedOrigins.has(event.origin)) return;

      // Handle auth request from iframe
      if (event.data?.type === 'REQUEST_AUTH' && user?.username) {
        // Find the iframe that sent the request and respond to it
        const sourceWindow = event.source as Window;
        if (sourceWindow) {
          sourceWindow.postMessage({
            type: 'HUD_USER_AUTH',
            email: user.username
          }, event.origin);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [trustedOrigins, user?.username]);

  // Handle iframe load - scroll to top and send user auth via postMessage
  const handleIframeLoad = (wId: string, iframeUrl: string) => {
    window.scrollTo(0, 0);

    // Send authenticated user's email to the iframe via postMessage
    // This allows embedded apps to do proper access control
    if (user?.username) {
      const iframe = iframeRefs.current[wId];
      if (iframe?.contentWindow) {
        try {
          const targetOrigin = new URL(iframeUrl).origin;
          iframe.contentWindow.postMessage({
            type: 'HUD_USER_AUTH',
            email: user.username
          }, targetOrigin);
        } catch (err) {
          console.warn('Failed to send auth message to iframe:', err);
        }
      }
    }
  };

  // Render mini-widgets into header portal
  const miniWidgetsPortalTarget = document.getElementById('mini-widgets-portal');
  const miniWidgetsPortal = miniWidgetsPortalTarget ? createPortal(
    <div className="mini-widgets-header">
      {widgets.map(widget => (
        <MiniWidget
          key={widget.id}
          config={widget}
          data={widgetData[widget.id] || { status: 'loading', lastUpdated: '', stats: [], alerts: [] }}
          isActive={widget.id === widgetId}
          onClick={() => handleMiniWidgetClick(widget)}
          onStatClick={(filterValue) => handleStatClickForWidget(widget.id, filterValue)}
        />
      ))}
    </div>,
    miniWidgetsPortalTarget
  ) : null;

  // Render refresh controls into header portal
  const portalTarget = document.getElementById('refresh-controls-portal');
  const refreshControlsPortal = portalTarget ? createPortal(
    <div className="refresh-status">
      <div className="last-refresh">
        <span className="refresh-label">Last Update:</span>
        <span className="refresh-time">
          {formatRelativeTime(lastRefresh.toISOString())}
          {isRefreshing && ' (refreshing...)'}
        </span>
      </div>
      <div className="refresh-controls">
        <label className="toggle-label">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          <span className="toggle-slider"></span>
          Auto
        </label>
        <select
          className="refresh-select"
          value={refreshInterval}
          onChange={(e) => setRefreshInterval(Number(e.target.value))}
          disabled={!autoRefresh}
        >
          <option value={30}>30s</option>
          <option value={60}>1m</option>
          <option value={120}>2m</option>
          <option value={300}>5m</option>
        </select>
        <button
          className="refresh-button"
          onClick={fetchAll}
          disabled={isRefreshing}
        >
          ↻
        </button>
      </div>
    </div>,
    portalTarget
  ) : null;

  if (!activeWidget) {
    return (
      <div className="focused-dashboard">
        {miniWidgetsPortal}
        {refreshControlsPortal}
        <div className="focused-error">
          <p>Widget not found</p>
          <button className="back-button" onClick={() => navigate('/')}>
            ← Back to Mission Control
          </button>
        </div>
      </div>
    );
  }

  // Check if current widget has a URL configured
  const currentWidgetUrl = buildIframeUrl(widgetId || '', true);

  return (
    <div className="focused-dashboard">
      {miniWidgetsPortal}
      {refreshControlsPortal}

      {/* Embedded dashboard iframes - one per visited widget, show/hide */}
      <div className="focused-content">
        {Array.from(visitedWidgets).map(wId => {
          const widget = widgets.find(w => w.id === wId);
          const isActive = wId === widgetId;
          // Only include deep link params (sub-path + query params) for the active widget
          const iframeUrl = buildIframeUrl(wId, isActive);
          const filter = widgetFilters[wId] || '';

          if (!iframeUrl) {
            // Only show "not configured" for active widget
            if (isActive) {
              return (
                <div key={wId} className="no-url-message">
                  <p>No dashboard URL configured for {widget?.name}</p>
                  <p>Go to Settings to configure the drill-down URL.</p>
                  <button className="back-button" onClick={() => navigate('/')}>
                    ← Back to Mission Control
                  </button>
                </div>
              );
            }
            return null;
          }

          // Include subPath and search in key for active widget to reload on deep link change
          const iframeKey = isActive
            ? `${wId}-${filter}-${theme}-${subPath || ''}-${location.search}`
            : `${wId}-${filter}-${theme}`;

          return (
            <iframe
              key={iframeKey}
              ref={(el) => { iframeRefs.current[wId] = el; }}
              src={iframeUrl}
              title={widget?.name || wId}
              className="dashboard-iframe"
              allow="notifications; geolocation"
              style={{ display: isActive ? 'block' : 'none' }}
              onLoad={isActive ? () => handleIframeLoad(wId, iframeUrl) : undefined}
            />
          );
        })}

        {/* Show "not configured" if current widget has no URL and wasn't rendered above */}
        {!currentWidgetUrl && !visitedWidgets.has(widgetId || '') && (
          <div className="no-url-message">
            <p>No dashboard URL configured for {activeWidget?.name}</p>
            <p>Go to Settings to configure the drill-down URL.</p>
            <button className="back-button" onClick={() => navigate('/')}>
              ← Back to Mission Control
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
